import React, { useEffect, useRef } from 'react'

import { css, Global, keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import FocusLock from 'react-focus-lock'

import { breakpoints, spacing } from '@cash-design-system/react'
import logger from '@cash-web/shared/util-logger'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { WebViewViewComponent } from '@squareup/customer-data-events-web'

import { ModalActionProvider } from './ModalActionProvider'
import { ModalActions, ModalVariant } from './ModalActions'
import { ModalContextProvider } from './ModalContext'
import { ModalPane } from './ModalPane'

type DialogProps = {
  hideOverflow?: boolean
  disableAnimation?: boolean
  width?: string
  isHelpAction?: boolean
  closable?: boolean
  fixed?: boolean
  variant?: ModalVariant
}

export const Position = styled.div`
  z-index: 99999;
  position: fixed;
  inset: 0 0 0 0;
`

export const DialogScrollableBackground = styled.div<DialogProps>`
  ${breakpoints.tablet} {
    ${props =>
      !props.disableAnimation &&
      css`
        animation: ${keyframes({
            '0%': { background: 'rgba(0, 0, 0, 0)' },
            '100%': {
              background: props.theme.arcadeColors.semantic.backgroundDimmer,
              backdropFilter: 'blur(.06rem)',
            },
          })}
          200ms forwards;
      `}
    ${props =>
      props.isHelpAction &&
      css`
        border-radius: 1.5rem;
      `}
    background: ${props => props.theme.arcadeColors.semantic.backgroundDimmer};
    padding: ${spacing.defaultContent};
    /* stylelint-disable-next-line number-max-precision */
    filter: opacity(0.99999);
    position: fixed;
    overflow-y: auto;
    inset: 0 0 0 0;
    display: flex;
    justify-content: center;
    align-items: center;
    ${({ fixed }) =>
      fixed &&
      css`
        align-items: start;
      `}
    ${({ variant, theme: { spacing } }) =>
      variant !== 'receipt' &&
      css`
        padding: ${spacing.xxxl} ${spacing.empty};
      `}
  }
`

const popUp = keyframes`
    0% {
        transform: translateY(1rem);
    }
    100% {
        transform: translateY(0);
    }
`

export const Dialog = styled.div<DialogProps>`
  position: relative;
  background: ${props => props.theme.arcadeColors.component.modalBackground};
  min-height: 100vh;
  max-height: 100vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  ${({ variant, theme: { spacing } }) =>
    variant !== 'receipt' &&
    css`
      padding: ${spacing.xl};
    `}
  display: flex;
  flex-direction: column;

  &:focus-visible {
    outline: none;
  }

  ${breakpoints.tablet} {
    max-height: fit-content;
    border-radius: 1.5rem;

    ${props =>
      !props.disableAnimation &&
      css`
        animation: ${popUp} 200ms forwards;
      `}
    width: ${props => props.width};
    box-shadow: 0 12px 24px 0 #0000000a;
    ${({ variant, closable }) =>
      variant !== 'receipt' &&
      css`
        padding: ${closable === false ? '3.5rem 2.5rem' : '2.5rem 2.5rem 3.5rem 2.5rem'};
      `}
    min-height: unset;

    ${({ variant }) =>
      variant === 'default' &&
      `
        overflow: visible;
      `}
    ${({ fixed }) =>
      !fixed &&
      css`
        margin: auto 0;
      `}
      ${({ hideOverflow }) =>
      hideOverflow &&
      css`
        overflow: hidden;
      `}
  }
  ${({ variant, theme: { spacing } }) =>
    variant === 'receipt' &&
    css`
      overflow: hidden;
      ${breakpoints.tablet} {
        padding: ${spacing.empty};
      }
    `}
`

const ModalContent = styled.div<{ overflowScroll: boolean; variant: ModalVariant }>`
  ${({ overflowScroll }) => {
    return css`
      overflow-y: ${overflowScroll ? 'auto' : 'visible'};
      min-height: 0;
      flex: 1 1 auto;
      -webkit-overflow-scrolling: touch;
    `
  }}
`

const OverflowScrollFocusLock = styled(FocusLock)`
  overflow-y: auto;
`

export interface ModalProps extends DialogProps {
  onClose?: () => void
  onBack?: () => void
  closable?: boolean
  children?: React.ReactNode
  overflowScroll?: boolean
  dataTestId?: string
}

export const BodyOverFlowHidden = css`
  body {
    overflow: hidden;
  }
`
export const useModal = ({ onClose }: { onClose?: () => void }) => {
  const closeOnEscape: React.KeyboardEventHandler<HTMLDivElement> = event => {
    if (event.key === 'Escape') {
      onClose?.()
    }
  }

  return { closeOnEscape }
}

const WIDTHS: Record<ModalVariant, string | undefined> = {
  default: '31rem',
  receipt: '23rem',
  fullscreen: undefined,
}

export const Modal = React.forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      children,
      onClose,
      onBack,
      closable = true,
      disableAnimation = false,
      hideOverflow = false,
      variant = 'default',
      width = WIDTHS[variant],
      overflowScroll = false,
      isHelpAction,
      fixed = false,
      dataTestId,
    }: ModalProps,
    ref
  ) => {
    const { closeOnEscape } = useModal({ onClose })
    const logCdfEvent = useLogCashWebCdfEvent()

    const hasLoggedShowModalRef = useRef(false)
    useEffect(() => {
      if (!hasLoggedShowModalRef.current) {
        logCdfEvent(WebViewViewComponent({ componentName: 'Modal', componentType: 'Open' }))
        hasLoggedShowModalRef.current = true
      }
    }, [logCdfEvent])

    // If variant is fullscreen, render ModalPane
    if (variant === 'fullscreen') {
      return (
        <ModalPane onBack={onBack} onClose={onClose} dataTestId={dataTestId ?? 'fullscreen-modal'}>
          {children}
        </ModalPane>
      )
    }

    return (
      <>
        <Global styles={BodyOverFlowHidden} />
        <Position>
          <DialogScrollableBackground
            data-testid={'modal-background'}
            onClick={() => {
              if (closable) {
                logger.addBreadcrumb('Close modal')
                onClose?.()
              }
            }}
            disableAnimation={disableAnimation}
            isHelpAction={isHelpAction}
            fixed={fixed}
            variant={variant}
            ref={ref}
          >
            {/* we don't want any clicks on the dialog to propagate and force the window closed */}
            <OverflowScrollFocusLock
              as={Dialog}
              lockProps={{
                onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => e.stopPropagation(),
                onKeyDown: closeOnEscape,
                role: 'dialog',
                'data-testid': dataTestId,
                disableAnimation: disableAnimation,
                width: width,
                fixed: fixed,
                ariaModal: true,
                closable,
                variant,
                hideOverflow,
              }}
            >
              <ModalActionProvider>
                {closable ? <ModalActions onClose={onClose} onBack={onBack} variant={variant} /> : null}
                <ModalContent overflowScroll={overflowScroll} variant={variant}>
                  <ModalContextProvider>{children}</ModalContextProvider>
                </ModalContent>
              </ModalActionProvider>
            </OverflowScrollFocusLock>
          </DialogScrollableBackground>
        </Position>
      </>
    )
  }
)
